import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { money, fmtDate, originBase, shareLinkText, linkValue, resizeImage } from '../events/util';
import FiltroTipoLote, { filtrarPorTipo, tipoInicial } from '../components/FiltroTipoLote';
import LinkGenerado from '../components/LinkGenerado';
import BotonGenerarLink from '../components/BotonGenerarLink';
import BotonCompartirLink from '../components/BotonCompartirLink';

const API = process.env.REACT_APP_API_URL || '';

export default function RrppPanel() {
  const { user, logout } = useAuth();
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [openEvent, setOpenEvent] = useState(null);

  useEffect(() => { api.get('/api/events').then(setEvents).catch(() => {}); }, []); // eslint-disable-line

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* header */}
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--ink-100)', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/assets/tukipass-icon.png" alt="" style={{ height: 30 }} />
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>Conectado como</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: '#fff' }}>{user?.name}</span>
              <span className="lb-status lb-status--used">Vendedor</span>
            </div>
          </div>
        </div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={logout}>Cerrar sesión</button>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 28px' }}>
        {openEvent ? (
          <RrppEventDetail event={openEvent} api={api} user={user} onBack={() => setOpenEvent(null)} />
        ) : (
          <>
            <div className="lb-display" style={{ fontSize: 24 }}>Panel RRPP · {user?.name}</div>
            <div className="lb-eyebrow" style={{ marginTop: 6, marginBottom: 24 }}>Tus eventos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {events.map(ev => <RrppEventCard key={ev.id} ev={ev} api={api} user={user} onOpen={() => setOpenEvent(ev)} />)}
              {events.length === 0 && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>No tenés eventos asignados todavía.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RrppEventCard({ ev, api, user, onOpen }) {
  const [stats, setStats] = useState({ gen: 0, vendido: 0 });
  useEffect(() => {
    api.get(`/api/rrpp-links?event_id=${ev.id}`).then(links => {
      const mine = links.filter(l => l.rrpp_id === user.id);
      // La venta se cuenta al GENERAR el link (max_uses), no cuando el comprador lo redime:
      // el RRPP ya es responsable de esa plata aunque el comprador después no venga.
      const gen = mine.reduce((a, l) => a + (l.max_uses || 0), 0);
      const vendido = mine.reduce((a, l) => a + linkValue(l), 0);
      setStats({ gen, vendido });
    }).catch(() => {});
  }, [ev.id]); // eslint-disable-line

  return (
    <div className="ev-card" onClick={onOpen}>
      <img src="/assets/tukipass-icon.png" alt="" className="lb-watermark" style={{ width: 150, right: -24, bottom: -24 }} />
      <div style={{ padding: '20px 22px', position: 'relative' }}>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', textTransform: 'capitalize' }}>{fmtDate(ev.date)}</div>
        <div className="lb-h" style={{ fontSize: 18, marginTop: 4 }}>{ev.name}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 3 }}>{[ev.start_time, ev.end_time].filter(Boolean).join(' — ') || '—'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Mini label="Generadas" value={stats.gen} />
          <Mini label="Vendido" value={money(stats.vendido)} small />
        </div>
        <button className="lb-btn lb-btn--primary lb-btn--block lb-btn--sm" style={{ marginTop: 16 }}>Generar →</button>
      </div>
    </div>
  );
}

function Mini({ label, value, small }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-600)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: small ? 14 : 20, color: '#fff', marginTop: 3 }}>{value}</div>
    </div>
  );
}

function RrppEventDetail({ event, api, user, onBack }) {
  const [full, setFull] = useState(null);
  const [links, setLinks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lotId, setLotId] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('ingreso');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [coupon, setCoupon] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  // Mientras el servidor contesta: el botón se pone gris y se va llenando, y no acepta
  // otro toque. Ese segundo de espera era el que terminaba en dos links generados — y
  // cada link cuenta como venta en la rendición del RRPP.
  const [generando, setGenerando] = useState(false);
  const generandoRef = useRef(false);
  const [openLink, setOpenLink] = useState(null);
  const [tab, setTab] = useState('generar'); // generar | venta | links | cumples | mesas
  const [birthdays, setBirthdays] = useState([]);
  const [bf, setBf] = useState({ first_name: '', last_name: '' });
  const [bdAttested, setBdAttested] = useState(false); // ya declaró la autorización para este evento
  // Cupo de la lista: se muestra y frena el botón, así nadie carga un nombre para que
  // se lo rechacen. El freno de verdad está en el servidor.
  const [bdCupo, setBdCupo] = useState(null);
  const [bdAttest, setBdAttest] = useState(false);      // casilla de declaración (primera carga)
  const [mesaSectors, setMesaSectors] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [mesaSectorId, setMesaSectorId] = useState('');
  const [mesaId, setMesaId] = useState('');
  const [mesaResult, setMesaResult] = useState(null);
  const [rendition, setRendition] = useState(null); // estado de MI rendición en este evento
  const [upBusy, setUpBusy] = useState(false);
  const loadRendition = () => api.get(`/api/rrpp-renditions?event_id=${event.id}`).then(rows => setRendition((rows || [])[0] || null)).catch(() => {});

  // LA PROMO LA CALCULA EL SERVIDOR, con la misma función que usa al generar el link:
  // así el total que ve el RRPP antes de generar es el que después cobra y rinde.
  const [cot, setCot] = useState(null);
  useEffect(() => {
    if (!lotId) { setCot(null); return; }
    let vivo = true;
    const t = setTimeout(() => {
      api.get(`/api/rrpp-links/cotizar?event_id=${event.id}&lot_id=${lotId}&max_uses=${qty}`)
        .then(c => { if (vivo) setCot(c); })
        .catch(() => { if (vivo) setCot(null); });
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [lotId, qty, event.id]); // eslint-disable-line

  const loadLinks = () => api.get(`/api/rrpp-links?event_id=${event.id}`).then(r => setLinks(r.filter(l => l.rrpp_id === user.id))).catch(() => {});
  const loadBirthdays = () => {
    api.get(`/api/birthdays?event_id=${event.id}`).then(setBirthdays).catch(() => {});
    api.get(`/api/birthdays/attested?event_id=${event.id}`).then(d => setBdAttested(!!d.attested)).catch(() => {});
  };
  const loadMesas = () => {
    api.get(`/api/mesas/sectors?event_id=${event.id}`).then(setMesaSectors).catch(() => {});
    api.get(`/api/mesas?event_id=${event.id}`).then(setMesas).catch(() => {});
  };
  useEffect(() => {
    api.get(`/api/events/${event.id}`).then(e => {
      setFull(e);
      const sellable = (e.lots || []).filter(l => l.roles !== 'owners' && l.status !== 'sold_out' && l.status !== 'paused' && !(Array.isArray(l.channels) && l.channels.includes('boleteria')));
      // El filtro arranca en Ingresos: el lote inicial tiene que salir de ESE grupo, o
      // el selector abriría con uno elegido que no está en la lista.
      const inicial = tipoInicial(sellable);
      setFiltroTipo(inicial);
      const visibles = filtrarPorTipo(sellable, inicial);
      if (visibles.length) setLotId(visibles[0].id);
    }).catch(() => {});
    loadLinks();
    loadBirthdays();
    loadCupo();
    loadMesas();
    loadRendition();
    api.get(`/api/tickets?event_id=${event.id}`).then(setTickets).catch(() => {});
  }, [event.id]); // eslint-disable-line

  // ¿hay mesas que el RRPP pueda generar? (roles='all'). Si no, no se muestra la pestaña.
  const canDoMesas = mesas.some(m => m.roles === 'all');
  const mesaAvailable = mesas.filter(m => m.sector_id === mesaSectorId && m.status === 'available' && m.roles === 'all');
  const myMesas = mesas.filter(m => m.status === 'sold' && m.generated_by === user.id);
  const mesaSel = mesas.find(m => m.id === mesaId);

  async function generateMesa() {
    if (!mesaId) return;
    try { const r = await api.post(`/api/mesas/${mesaId}/emit`, {}); setMesaResult({ mesa: mesaSel, ...r }); setMesaId(''); loadMesas(); }
    catch (e) { alert(e.message); }
  }

  const loadCupo = () => api.get(`/api/birthdays/cupo?event_id=${event.id}`).then(setBdCupo).catch(() => {});

  async function addBirthday() {
    if (!bf.first_name.trim() || !bf.last_name.trim()) { alert('Nombre y apellido'); return; }
    if (!bdAttested && !bdAttest) { alert('Primero marcá la declaración de que contás con la autorización del padre/madre/tutor.'); return; }
    try {
      await api.post('/api/birthdays', { event_id: event.id, first_name: bf.first_name, last_name: bf.last_name, attest: bdAttest });
      setBf({ first_name: '', last_name: '' }); setBdAttested(true); loadBirthdays(); loadCupo();
    }
    catch (e) { alert(e.message); loadCupo(); }
  }
  async function subirComprobante(file) {
    if (!file) return;
    if (!/^(image\/|application\/pdf)/.test(file.type)) { alert('Solo se aceptan imágenes (JPG, PNG, WEBP) o PDF.'); return; }
    if (file.size > 6 * 1024 * 1024) { alert('El archivo supera el máximo de 6 MB.'); return; }
    setUpBusy(true);
    try {
      // Las IMÁGENES se reducen antes de subir. Una foto de 6 MB viaja como ~8 MB de
      // JSON (base64 infla un 33%), y desde un celular a las 4 de la mañana esa subida
      // se corta a mitad: el 8/8 quedaron 2 registros de "request aborted". Reducida a
      // 1600 px de ancho, el comprobante sigue perfectamente legible y pesa ~10 veces
      // menos. Fondo blanco: una captura con transparencia y texto oscuro sobre negro
      // quedaría ilegible. Los PDF van tal cual (no se pueden recomprimir así).
      const esImagen = file.type.startsWith('image/');
      const dataUri = esImagen
        ? await resizeImage(file, 1600, 0.82, '#fff')
        : await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(new Error('No se pudo leer el archivo')); r.readAsDataURL(file); });
      await api.post('/api/rrpp-renditions/comprobante', { event_id: event.id, comprobante: dataUri });
      await loadRendition();
    } catch (e) {
      // "Failed to fetch" acá casi siempre es la subida cortada, no un error del
      // servidor: decirlo así evita que el RRPP crea que ya subió el comprobante.
      const msg = /failed to fetch|network/i.test(e?.message || '')
        ? 'Se cortó la subida (conexión inestable). Probá de nuevo.'
        : (e.message || 'No se pudo subir el comprobante');
      alert(msg);
    } finally { setUpBusy(false); }
  }
  async function delBirthday(id) {
    try { await api.del(`/api/birthdays/${id}`); loadBirthdays(); } catch (e) { alert(e.message); }
  }

  // Boletería no la genera el RRPP: se vende solo en la caja de boletería.
  const lots = (full?.lots || []).filter(l => l.roles !== 'owners' && !(Array.isArray(l.channels) && l.channels.includes('boleteria')));
  const sellable = lots.filter(l => l.status !== 'sold_out' && l.status !== 'paused');
  // Al filtrar hay que soltar el lote elegido si dejo de estar a la vista.
  const lotesVisibles = filtrarPorTipo(sellable, filtroTipo);
  const cambiarFiltro = (k) => {
    setFiltroTipo(k);
    const visibles = filtrarPorTipo(sellable, k);
    if (!visibles.some(l => l.id === lotId)) { setLotId(visibles[0]?.id || ''); setCouponInfo(null); }
  };
  const lot = lots.find(l => l.id === lotId);
  const valorLote = lot ? qty * Number(lot.price) : 0;
  // Con cupón, el descuento va POR ENTRADA (el link puede ser de varias): así el
  // precio que ve el RRPP coincide con lo que después paga cada comprador.
  const precioUnit = lot ? Number(lot.price) : 0;
  const descPorEntrada = !couponInfo?.valid ? 0
    : (couponInfo.type === 'fixed'
        ? Math.min(precioUnit, Number(couponInfo.amount) || 0)
        : Math.round(precioUnit * (Number(couponInfo.percent) || 0) / 100));
  // El cupon vale para UNA entrada del link: la primera sale con descuento y el
  // resto al precio normal (es el cupon de una persona, no del grupo).
  // La promo se resta aparte (los dos beneficios se suman), y solo si la cotización es
  // de ESTA cantidad: mientras llega la nueva no se muestra el descuento de la anterior.
  const promoMonto = cot && cot.qty === qty && cot.promo ? Number(cot.promo.monto) : 0;
  const valorConCupon = Math.max(0, Math.max(0, precioUnit - descPorEntrada) + precioUnit * Math.max(0, qty - 1) - promoMonto);
  const hayDescuento = descPorEntrada > 0 || promoMonto > 0;

  // La venta se cuenta al GENERAR el link (max_uses = QR puestos a la venta), no al redimirlo:
  // de esa plata el RRPP es responsable aunque el comprador no venga (la tiene que rendir).
  const generadas = links.reduce((a, l) => a + (l.max_uses || 0), 0);
  const mesaVendido = myMesas.reduce((a, m) => a + Number(m.price || 0), 0); // mesas que generó este RRPP
  const vendido = links.reduce((a, l) => a + linkValue(l), 0) + mesaVendido;
  const pct = full?.commission_pct == null ? 10 : Number(full.commission_pct);
  const comision = Math.round(vendido * pct / 100);
  const aRendir = vendido - comision;

  // Cupón: el backend ya lo aceptaba al crear el link, pero el RRPP no tenía dónde
  // ponerlo — solo el dueño desde el evento. Mismo mecanismo que en GenerarEntradas.
  async function validateCoupon() {
    if (!coupon.trim()) { setCouponInfo(null); return; }
    if (!lotId) { setCouponInfo({ valid: false, error: 'Elegí primero el lote' }); return; }
    try {
      const r = await fetch(`${API}/api/public/coupon/${coupon.trim().toUpperCase()}?event_id=${event.id}&lot_id=${lotId}`);
      const d = await r.json();
      setCouponInfo(r.ok ? d : { valid: false, error: d.error });
    } catch { setCouponInfo({ valid: false, error: 'Error' }); }
  }

  async function generate() {
    // El ref frena incluso dos toques en el mismo instante: un setState no se ve
    // reflejado hasta el próximo render, y ahí ya salieron dos links.
    if (!lotId || generandoRef.current) return;
    generandoRef.current = true;
    setGenerando(true);
    try {
      const created = await api.post('/api/rrpp-links', {
        event_id: event.id, lot_id: lotId, max_uses: qty, note: note.trim() || null,
        coupon_code: couponInfo?.valid ? coupon.trim().toUpperCase() : null,
      });
      // lot_price viaja para que la ventana del link muestre su valor con linkValue(), la
      // misma cuenta que usan la pestaña de venta y la rendición.
      setJustCreated({ ...created, lot_name: lot?.name, lot_price: lot?.price });
      setNote(''); setCoupon(''); setCouponInfo(null); setCopied(false);
      loadLinks();
    } catch (e) { alert(e.message); }
    finally { generandoRef.current = false; setGenerando(false); }
  }
  function resetForm() {
    setQty(1); setNote(''); setCoupon(''); setCouponInfo(null); setJustCreated(null); setCopied(false);
  }
  // El RRPP NO puede eliminar sus links: rinde por lo generado, así que borrarlos le
  // borraría la deuda. Lo pide a quien maneja el evento. (Antes había un ✕ acá.)
  function linkUrl(code) { return `${originBase()}/l/${code}`; }
  function copyLink(code) {
    navigator.clipboard?.writeText(linkUrl(code));
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }
  async function shareLink(link) {
    const url = linkUrl(link.code);
    const text = shareLinkText({ eventName: event.name, eventDate: event.date, cantidad: link.max_uses, lote: link.lot_name });
    if (navigator.share) {
      try { await navigator.share({ title: event.name, text, url }); } catch (e) { /* cancelado */ }
    } else {
      try { await navigator.clipboard?.writeText(`${text}\n${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
    }
  }

  return (
    <div className="lb-fade">
      <button onClick={onBack} className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginBottom: 14 }}>← Mis eventos</button>
      <div className="lb-display" style={{ fontSize: 26 }}>{event.name}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', textTransform: 'capitalize', marginBottom: 24 }}>{fmtDate(event.date)}</div>

      {(() => {
        const past = event.date && new Date(event.date) < new Date(new Date().toDateString());
        const st = rendition?.status || 'pendiente';
        if (past && st === 'pendiente' && aRendir > 0) return (
          <div style={{ marginBottom: 20, padding: '14px 18px', borderRadius: 8, border: '1px solid var(--red-500)', background: 'rgba(200,16,46,0.08)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Tenés una rendición pendiente de {money(aRendir)}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 2 }}>El evento ya pasó. Rendí lo que corresponde y subí el comprobante en "Mi venta".</div>
            </div>
            <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setTab('venta')}>Ir a rendir →</button>
          </div>
        );
        return null;
      })()}

      <div className="lb-subtabs" style={{ marginBottom: 22 }}>
        <button className={tab === 'generar' ? 'active' : ''} onClick={() => setTab('generar')}>Generar</button>
        <button className={tab === 'venta' ? 'active' : ''} onClick={() => setTab('venta')}>Mi venta</button>
        <button className={tab === 'links' ? 'active' : ''} onClick={() => setTab('links')}>Mis links</button>
        <button className={tab === 'cumples' ? 'active' : ''} onClick={() => setTab('cumples')}>Cumpleaños</button>
        {canDoMesas && <button className={tab === 'mesas' ? 'active' : ''} onClick={() => setTab('mesas')}>Mesas</button>}
      </div>

      {tab === 'generar' && (
        <div className="lb-card" style={{ padding: '24px 26px', maxWidth: 520 }}>
          <div className="lb-eyebrow" style={{ marginBottom: 16 }}>Generar QRs para vender</div>
          <div className="lb-field" style={{ marginBottom: 16 }}>
            <div className="lb-label">Tipo de entrada / lote</div>
            <FiltroTipoLote lotes={sellable} valor={filtroTipo} onChange={cambiarFiltro} />
            <select className="lb-select" value={lotId} onChange={e => { setLotId(e.target.value); setCouponInfo(null); }}>
              {lotesVisibles.length === 0 && <option value="">Sin lotes disponibles</option>}
              {lotesVisibles.map(l => <option key={l.id} value={l.id}>{l.name} · {money(l.price)}</option>)}
            </select>
          </div>
          <div className="lb-field" style={{ marginBottom: 16 }}>
            <div className="lb-label">Cantidad</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 999 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => Math.max(1, q - 1))}>–</button>
              <span style={{ minWidth: 50, textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18 }}>{qty}</span>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => q + 1)}>+</button>
            </div>
          </div>
          <div className="lb-field" style={{ marginBottom: 16 }}>
            <div className="lb-label">Nota · para quién (opcional)</div>
            <input className="lb-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: Juan de Funes" />
          </div>
          {/* Cupón: el backend ya lo aceptaba, pero el RRPP no tenía dónde cargarlo. */}
          <div className="lb-field" style={{ marginBottom: 16 }}>
            <div className="lb-label">Cupón de descuento (opcional)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="lb-input" value={coupon} onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }} placeholder="Ej: JUNIO20" />
              <button type="button" className="lb-btn lb-btn--ghost lb-btn--sm" onClick={validateCoupon} disabled={!coupon.trim()}>Aplicar</button>
            </div>
            {couponInfo && (couponInfo.valid
              ? <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 6 }}>✓ {couponInfo.type === 'fixed' ? `${money(couponInfo.amount)} de descuento` : `${couponInfo.percent}% de descuento`}{couponInfo.name ? ` · ${couponInfo.name}` : ''} · se aplica a 1 entrada del link</div>
              : <div style={{ fontSize: 12, color: 'var(--red-300)', marginTop: 6 }}>{couponInfo.error}</div>)}
          </div>
          <div style={{ padding: '14px 16px', background: 'var(--ink-050)', border: '1px solid ' + (promoMonto > 0 ? 'rgba(46,204,113,0.45)' : 'var(--border)'), borderRadius: 4, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="lb-eyebrow">Total{hayDescuento ? ' con descuento' : ''}</span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                {hayDescuento && <span style={{ fontSize: 14, color: 'var(--ink-600)', textDecoration: 'line-through' }}>{money(valorLote)}</span>}
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: '#fff' }}>{money(valorConCupon)}</span>
              </span>
            </div>
            {/* Lo que el RRPP cobra por este link: el mismo número que después aparece en
                su pestaña de venta y en la rendición. */}
            {promoMonto > 0 && (
              <div style={{ fontSize: 12.5, color: '#2ecc71', marginTop: 8 }}>
                ✓ Promoción aplicada · {cot.promo.detalle} · −{money(promoMonto)}
              </div>
            )}
          </div>
          {/* Bloqueado mientras hay un link recién generado. En el teléfono del RRPP el
              resultado quedaba fuera de pantalla y volvía a apretar: cada link cuenta
              como venta, así que esos duplicados le inflaban la rendición. */}
          <BotonGenerarLink generando={generando} hecho={!!justCreated} disabled={!lotId} onClick={generate} />

          <LinkGenerado
            link={justCreated}
            url={justCreated ? linkUrl(justCreated.code) : ''}
            copiado={copied}
            onCopiar={() => copyLink(justCreated.code)}
            onCompartir={() => shareLink(justCreated)}
            onOtro={resetForm}
            onCerrar={resetForm}
          />
        </div>
      )}

      {tab === 'venta' && (
        <div className="lb-card" style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 520 }}>
          <Stat label="Generadas (QR a la venta)" value={generadas} />
          {myMesas.length > 0 && <Stat label={`Mesas (${myMesas.length})`} value={money(mesaVendido)} />}
          <Stat label="Vendido" value={money(vendido)} />
          <Stat label={`Tu comisión (${pct}%)`} value={money(comision)} />
          <Stat label="A rendir" value={money(aRendir)} accent />
          <div style={{ fontSize: 11, color: 'var(--ink-600)', lineHeight: 1.5 }}>La venta se cuenta al <b>generar</b> el link (aunque el comprador todavía no lo haya abierto). <b>A rendir</b> es lo que tenés que entregarle al organizador.</div>

          {aRendir > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div className="lb-eyebrow" style={{ marginBottom: 8 }}>Comprobante de transferencia</div>
              {(() => {
                const st = rendition?.status || 'pendiente';
                if (st === 'confirmada') return <div style={{ fontSize: 13, color: '#2ecc71', fontWeight: 600 }}>✓ Rendición confirmada por el organizador.</div>;
                if (st === 'en_revision') return (
                  <div>
                    <div style={{ fontSize: 13, color: '#f1c40f', fontWeight: 600 }}>⏳ En revisión — el organizador tiene que confirmarla.</div>
                    <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 10, cursor: 'pointer', display: 'inline-block' }}>
                      {upBusy ? 'Subiendo…' : 'Reemplazar comprobante'}
                      <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: 'none' }} disabled={upBusy} onChange={e => subirComprobante(e.target.files?.[0])} />
                    </label>
                  </div>
                );
                return (
                  <div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.5, marginBottom: 10 }}>Subí el comprobante de la transferencia. Al subirlo, la rendición queda <b>en revisión</b> hasta que el organizador la confirme.</div>
                    <label className="lb-btn lb-btn--primary lb-btn--sm" style={{ cursor: 'pointer', display: 'inline-block' }}>
                      {upBusy ? 'Subiendo…' : '📎 Subir comprobante'}
                      <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: 'none' }} disabled={upBusy} onChange={e => subirComprobante(e.target.files?.[0])} />
                    </label>
                    <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 8 }}>Imagen (JPG, PNG, WEBP) o PDF · máx. 6 MB.</div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {tab === 'links' && (
      <>
      {/* Se explica en vez de dejar que lo descubran: antes había un ✕ acá y su
          desaparición sin aviso parece un error del sistema. */}
      {links.length > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', margin: '0 0 10px', lineHeight: 1.5 }}>
          Los links generados no se pueden eliminar. Si necesitás anular uno, pedíselo a quien maneja el evento.
        </div>
      )}
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.1fr 1.2fr 0.9fr 0.9fr auto' }}>
          <span>Lote</span><span>Código</span><span>Usos</span><span>Valor</span><span style={{ textAlign: 'right' }}>Asistentes</span>
        </div>
        {links.map(l => {
          const left = l.max_uses - l.used_count;
          const open = openLink === l.id;
          const asists = tickets.filter(t => t.link_id === l.id);
          return (
            <div key={l.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="lb-row lb-linkrow" style={{ gridTemplateColumns: '1.1fr 1.2fr 0.9fr 0.9fr auto', borderBottom: 0 }}>
                <span>
                  <span style={{ display: 'block', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', color: '#fff' }}>{l.lot_name}</span>
                  {l.note && <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{l.note}</span>}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--red-400)' }}>/{l.code}</span>
                  <button className="lb-iconbtn" style={{ width: 24, height: 24, fontSize: 11 }} title="Copiar link" onClick={() => copyLink(l.code)}>⧉</button>
                </span>
                <span style={{ color: left === 0 ? 'var(--ink-600)' : 'var(--ink-800)' }}>{l.used_count}/{l.max_uses} <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>· {left} libres</span></span>
                <span style={{ color: 'var(--ink-800)' }}>{money(l.lot_price)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                  {/* Para reenviarle el link a quien le compró, sin generar uno nuevo:
                      cada link generado cuenta como venta en su rendición. */}
                  <BotonCompartirLink link={l} event={event} compacto />
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setOpenLink(open ? null : l.id)}>Ver {open ? '▾' : '▸'}</button>
                </div>
              </div>
              {open && (
                <div style={{ padding: '6px 0 16px' }}>
                  {asists.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-600)', padding: '6px 0' }}>Sin asistentes todavía.</div>}
                  {asists.map(t => (
                    <div key={t.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1fr 1fr', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ color: '#fff', fontSize: 13 }}>{t.buyer_name} {t.buyer_lastname}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-700)' }}>{t.qr_code}</span>
                      <span className={'lb-status lb-status--dot lb-status--' + (t.status === 'usado' ? 'used' : t.status === 'pagado' ? 'paid' : 'gen')}>{t.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {links.length === 0 && <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Todavía no generaste links para este evento.</div>}
      </div>
      </>
      )}

      {tab === 'cumples' && (
      <div style={{ maxWidth: 560 }}>
        <div className="lb-card" style={{ padding: '20px 22px' }}>
          <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Agregar cumpleañero</div>
          {!bdAttested && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.45, cursor: 'pointer', marginBottom: 14, padding: '12px 14px', border: '1px solid rgba(236,38,143,0.4)', borderRadius: 6, background: 'rgba(236,38,143,0.05)' }}>
              <input type="checkbox" checked={bdAttest} onChange={e => setBdAttest(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Declaro que cuento con la <b style={{ color: '#fff' }}>autorización del padre, madre o tutor</b> de cada invitado para cargar sus datos. <span style={{ color: 'var(--ink-600)' }}>(Se pide una sola vez por evento.)</span></span>
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="lb-input" style={{ flex: 1, minWidth: 130 }} placeholder="Apellido" value={bf.last_name} onChange={e => setBf({ ...bf, last_name: e.target.value })} />
            <input className="lb-input" style={{ flex: 1, minWidth: 130 }} placeholder="Nombre" value={bf.first_name} onChange={e => setBf({ ...bf, first_name: e.target.value })} onKeyDown={e => e.key === 'Enter' && addBirthday()} />
            <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={addBirthday} disabled={!!bdCupo?.lleno}>+ Agregar</button>
          </div>
          {bdCupo?.lleno && <div style={{ fontSize: 12, color: 'var(--red-400)', marginTop: 8 }}>La lista está completa. No se pueden anotar más cumpleañeros.</div>}
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 8 }}>Se suman a la lista del evento. En la puerta (boletería) se les entrega su entrada gratis.</div>
        </div>

        <div className="lb-eyebrow" style={{ margin: '24px 0 10px' }}>Mis cumpleañeros ({birthdays.length})</div>
        <div className="lb-card" style={{ padding: '6px 20px' }}>
          {birthdays.map(b => (
            <div key={b.id} className="lb-row" style={{ gridTemplateColumns: '1fr auto auto', alignItems: 'center' }}>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{b.last_name}, <span style={{ fontWeight: 400, color: 'var(--ink-800)' }}>{b.first_name}</span></span>
              <span className={'lb-status lb-status--' + (b.status === 'entregado' ? 'used' : 'gen')} style={{ marginRight: 10 }}>{b.status === 'entregado' ? 'Ingresó' : 'Pendiente'}</span>
              {b.status === 'pending' ? <button className="lb-iconbtn" onClick={() => delBirthday(b.id)}>✕</button> : <span style={{ width: 24 }} />}
            </div>
          ))}
          {birthdays.length === 0 && <div style={{ padding: '18px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Todavía no cargaste cumpleañeros.</div>}
        </div>
      </div>
      )}

      {tab === 'mesas' && (
      <div style={{ maxWidth: 620 }}>
        <div className="lb-card" style={{ padding: '22px 24px' }}>
          <div className="lb-eyebrow" style={{ marginBottom: 14 }}>Generar mesa</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 10, alignItems: 'end' }}>
            <div className="lb-field"><div className="lb-label">Sector</div>
              <select className="lb-select" value={mesaSectorId} onChange={e => { setMesaSectorId(e.target.value); setMesaId(''); }}>
                <option value="">Sector</option>
                {mesaSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="lb-field"><div className="lb-label">Mesa disponible</div>
              <select className="lb-select" value={mesaId} onChange={e => setMesaId(e.target.value)} disabled={!mesaSectorId}>
                <option value="">{!mesaSectorId ? 'Elegí el sector' : mesaAvailable.length ? 'Elegí la mesa' : 'Sin mesas disponibles'}</option>
                {mesaAvailable.map(m => <option key={m.id} value={m.id}>{m.name} · {m.capacity} personas · {money(m.price)}</option>)}
              </select>
            </div>
            <button className="lb-btn lb-btn--primary" onClick={generateMesa} disabled={!mesaId}>Generar →</button>
          </div>
        </div>

        <div className="lb-eyebrow" style={{ margin: '24px 0 10px' }}>Mis mesas generadas ({myMesas.length})</div>
        <div className="lb-card" style={{ padding: '6px 20px' }}>
          {myMesas.map(m => (
            <div key={m.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1fr auto', alignItems: 'center' }}>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{m.sector_name ? `${m.sector_name} · ` : ''}{m.name}</span>
              <span style={{ color: 'var(--ink-800)', fontSize: 13 }}>{m.capacity} pers · {money(m.price)}</span>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { navigator.clipboard?.writeText(`${originBase()}/mesa/${m.public_code}`); }}>Copiar link</button>
            </div>
          ))}
          {myMesas.length === 0 && <div style={{ padding: '18px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Todavía no generaste mesas.</div>}
        </div>

        {mesaResult && (
          <div className="lb-modal-backdrop" onClick={() => setMesaResult(null)}>
            <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="lb-eyebrow">Mesa generada</div>
                <div className="lb-h" style={{ fontSize: 19, marginTop: 4 }}>{mesaResult.mesa?.name} · {mesaResult.mesa?.capacity || mesaResult.capacity} personas</div>
              </div>
              <div style={{ padding: '18px 24px' }}>
                <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Link para el comprador</div>
                <div style={{ fontSize: 12, color: 'var(--ink-800)', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 10 }}>{`${originBase()}/mesa/${mesaResult.public_code}`}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ flex: 1 }} onClick={() => { const u = `${originBase()}/mesa/${mesaResult.public_code}`; if (navigator.share) navigator.share({ url: u }).catch(() => {}); else navigator.clipboard?.writeText(u); }}>Compartir</button>
                  <button className="lb-btn lb-btn--white lb-btn--sm" style={{ flex: 1 }} onClick={() => navigator.clipboard?.writeText(`${originBase()}/mesa/${mesaResult.public_code}`)}>Copiar</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 10 }}>El comprador crea un PIN y cada asistente genera su entrada. La plata la rendís como el resto de tus ventas.</div>
              </div>
              <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'right' }}>
                <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setMesaResult(null)}>Listo</button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="lb-stat">
      <div className="lb-eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 32, color: accent ? 'var(--red-400)' : 'var(--white)' }}>{value}</div>
    </div>
  );
}
